/*
    LibreSpeed - Main
    by Federico Dossena
    https://github.com/librespeed/speedtest/
    GNU LGPLv3 License

    TypeScript port of speedtest.js used by https://speedtest.ecpi.net/.
    Behavior is unchanged except that the worker is bundled by Vite from
    ./speedtest_worker.js so it is served alongside the app's other assets.
*/

export interface SpeedtestServer
{
    name: string;
    server: string;
    dlURL: string;
    ulURL: string;
    pingURL: string;
    getIpURL: string;
    pingT?: number;
}

export interface SpeedtestData
{
    testState: number; // -1=not started, 0=starting, 1=download, 2=ping+jitter, 3=upload, 4=finished, 5=aborted
    dlStatus: string;
    ulStatus: string;
    pingStatus: string;
    jitterStatus: string;
    clientIp: string;
    dlProgress: number;
    ulProgress: number;
    pingProgress: number;
    testId: string | null;
}


export class Speedtest
{
    private serverList: SpeedtestServer[] = [];
    private selectedServer: SpeedtestServer | null = null;
    private settings: Record<string, unknown> = {};
    private state = 0; // 0=adding settings, 1=adding servers, 2=server selection done, 3=test running, 4=done
    private originalExtra: unknown;
    private selectServerCalled = false;
    private prevData: unknown;
    private worker: Worker | null = null;
    private updater: ReturnType<typeof setInterval> | null = null;

    onupdate?: (data: SpeedtestData) => void;
    onend?: (aborted: boolean) => void;
    onerror?: (message: string) => void;

    getState(): number
    {
        return this.state;
    }

    setParameter(parameter: string, value: unknown): void
    {
        if (this.state != 0)
            throw "You cannot change the test settings after adding server or starting the test";
        this.settings[parameter] = value;
        if (parameter === "temeletry_extra")
        {
            this.originalExtra = this.settings.telemetry_extra;
        }
    }

    private checkServerDefinition(server: SpeedtestServer): void
    {
        try
        {
            if (typeof server.name !== "string") throw "Name string missing from server definition (name)";
            if (typeof server.server !== "string") throw "Server address string missing from server definition (server)";
            if (server.server.charAt(server.server.length - 1) != "/") server.server += "/";
            if (server.server.indexOf("//") == 0) server.server = location.protocol + server.server;
            if (typeof server.dlURL !== "string") throw "Download URL string missing from server definition (dlURL)";
            if (typeof server.ulURL !== "string") throw "Upload URL string missing from server definition (ulURL)";
            if (typeof server.pingURL !== "string") throw "Ping URL string missing from server definition (pingURL)";
            if (typeof server.getIpURL !== "string") throw "GetIP URL string missing from server definition (getIpURL)";
        } catch
        {
            throw "Invalid server definition";
        }
    }

    addTestPoint(server: SpeedtestServer): void
    {
        this.checkServerDefinition(server);
        if (this.state == 0) this.state = 1;
        if (this.state != 1) throw "You can't add a server after server selection";
        this.settings.mpot = true;
        this.serverList.push(server);
    }

    addTestPoints(list: SpeedtestServer[]): void
    {
        for (let i = 0; i < list.length; i++) this.addTestPoint(list[i]);
    }

    loadServerList(url: string, result: (servers: SpeedtestServer[] | null) => void): void
    {
        if (this.state == 0) this.state = 1;
        if (this.state != 1) throw "You can't add a server after server selection";
        this.settings.mpot = true;
        const xhr = new XMLHttpRequest();
        xhr.onload = () =>
        {
            try
            {
                const servers: SpeedtestServer[] = JSON.parse(xhr.responseText);
                for (let i = 0; i < servers.length; i++) this.checkServerDefinition(servers[i]);
                this.addTestPoints(servers);
                result(servers);
            } catch
            {
                result(null);
            }
        };
        xhr.onerror = () => result(null);
        xhr.open("GET", url);
        xhr.send();
    }

    getSelectedServer(): SpeedtestServer
    {
        if (this.state < 2 || this.selectedServer == null) throw "No server is selected";
        return this.selectedServer;
    }

    setSelectedServer(server: SpeedtestServer): void
    {
        this.checkServerDefinition(server);
        if (this.state == 3) throw "You can't select a server while the test is running";
        this.selectedServer = server;
        this.state = 2;
    }

    selectServer(result?: (server: SpeedtestServer | null) => void): void
    {
        if (this.state != 1)
        {
            if (this.state == 0) throw "No test points added";
            if (this.state == 2) throw "Server already selected";
            if (this.state >= 3) throw "You can't select a server while the test is running";
        }
        if (this.selectServerCalled) throw "selectServer already called";
        else this.selectServerCalled = true;

        const select = (serverList: SpeedtestServer[], done: (server: SpeedtestServer | null) => void) =>
        {
            const PING_TIMEOUT = 2000;
            let USE_PING_TIMEOUT = true;
            if (/MSIE.(\d+\.\d+)/i.test(navigator.userAgent)) USE_PING_TIMEOUT = false;

            const ping = (url: string, cb: (t: number) => void) =>
            {
                url += (url.match(/\?/) ? "&" : "?") + "cors=true";
                const xhr = new XMLHttpRequest();
                const t = new Date().getTime();
                xhr.onload = () =>
                {
                    if (xhr.responseText.length == 0)
                    {
                        let instspd = new Date().getTime() - t;
                        try
                        {
                            const entries = performance.getEntriesByName(url) as PerformanceResourceTiming[];
                            const p = entries[entries.length - 1];
                            let d = p.responseStart - p.requestStart;
                            if (d <= 0) d = p.duration;
                            if (d > 0 && d < instspd) instspd = d;
                        } catch { /* ignore */ }
                        cb(instspd);
                    } else cb(-1);
                };
                xhr.onerror = () => cb(-1);
                xhr.open("GET", url);
                if (USE_PING_TIMEOUT)
                {
                    try
                    {
                        xhr.timeout = PING_TIMEOUT;
                        xhr.ontimeout = xhr.onerror;
                    } catch { /* ignore */ }
                }
                xhr.send();
            };

            const PINGS = 3, SLOW_THRESHOLD = 500;
            const checkServer = (server: SpeedtestServer, next: () => void) =>
            {
                let i = 0;
                server.pingT = -1;
                if (server.server.indexOf(location.protocol) == -1) next();
                else
                {
                    const nextPing = () =>
                    {
                        if (i++ == PINGS)
                        {
                            next();
                            return;
                        }
                        ping(server.server + server.pingURL, (t) =>
                        {
                            if (t >= 0)
                            {
                                if (t < server.pingT! || server.pingT == -1) server.pingT = t;
                                if (t < SLOW_THRESHOLD) nextPing();
                                else next();
                            } else next();
                        });
                    };
                    nextPing();
                }
            };

            let i = 0;
            const finish = () =>
            {
                let bestServer: SpeedtestServer | null = null;
                for (let j = 0; j < serverList.length; j++)
                {
                    if (serverList[j].pingT != -1 && (bestServer == null || serverList[j].pingT! < bestServer.pingT!))
                        bestServer = serverList[j];
                }
                done(bestServer);
            };
            const nextServer = () =>
            {
                if (i == serverList.length)
                {
                    finish();
                    return;
                }
                checkServer(serverList[i++], nextServer);
            };
            nextServer();
        };

        const CONCURRENCY = 6;
        const serverLists: SpeedtestServer[][] = [];
        for (let i = 0; i < CONCURRENCY; i++) serverLists[i] = [];
        for (let i = 0; i < this.serverList.length; i++) serverLists[i % CONCURRENCY].push(this.serverList[i]);
        let completed = 0;
        let bestServer: SpeedtestServer | null = null;
        for (let i = 0; i < CONCURRENCY; i++)
        {
            select(serverLists[i], (server) =>
            {
                if (server != null)
                {
                    if (bestServer == null || server.pingT! < bestServer.pingT!) bestServer = server;
                }
                completed++;
                if (completed == CONCURRENCY)
                {
                    this.selectedServer = bestServer;
                    this.state = 2;
                    if (result) result(bestServer);
                }
            });
        }
    }

    start(): void
    {
        if (this.state == 3) throw "Test already running";
        this.worker = new Worker(new URL("./speedtest_worker.js", import.meta.url));
        this.worker.onmessage = (e: MessageEvent) =>
        {
            if (e.data === this.prevData) return;
            else this.prevData = e.data;
            const data: SpeedtestData = JSON.parse(e.data);
            try
            {
                if (this.onupdate) this.onupdate(data);
            } catch (err)
            {
                console.error("Speedtest onupdate event threw exception: " + err);
            }
            if (data.testState >= 4)
            {
                try
                {
                    if (this.onend) this.onend(data.testState == 5);
                } catch (err)
                {
                    console.error("Speedtest onend event threw exception: " + err);
                }
                if (this.updater) clearInterval(this.updater);
                this.state = 4;
            }
        };
        this.worker.onerror = (e: ErrorEvent) =>
        {
            e.preventDefault();
            const message = e.message || "Unable to load the speed test worker";
            console.error("Speedtest worker error: " + message);
            this.dispose();
            this.state = 4;
            if (this.onerror) this.onerror(message);
        };
        this.updater = setInterval(() =>
        {
            this.worker?.postMessage("status");
        }, 200);
        if (this.state == 1)
            throw "When using multiple points of test, you must call selectServer before starting the test";
        if (this.state == 2 && this.selectedServer)
        {
            this.settings.url_dl = this.selectedServer.server + this.selectedServer.dlURL;
            this.settings.url_ul = this.selectedServer.server + this.selectedServer.ulURL;
            this.settings.url_ping = this.selectedServer.server + this.selectedServer.pingURL;
            this.settings.url_getIp = this.selectedServer.server + this.selectedServer.getIpURL;
            if (typeof this.originalExtra !== "undefined")
            {
                this.settings.telemetry_extra = JSON.stringify({
                    server: this.selectedServer.name,
                    extra: this.originalExtra
                });
            } else
                this.settings.telemetry_extra = JSON.stringify({ server: this.selectedServer.name });
        }
        this.state = 3;
        this.worker.postMessage("start " + JSON.stringify(this.settings));
    }

    abort(): void
    {
        if (this.state < 3) throw "You cannot abort a test that's not started yet";
        if (this.state < 4) this.worker?.postMessage("abort");
    }

    /** Stops the worker and polling timer (used when the hosting component unmounts). */
    dispose(): void
    {
        if (this.updater) clearInterval(this.updater);
        this.worker?.terminate();
        this.worker = null;
    }
}
